import { useState } from "react";
import {
  Typography,
  Box,
} from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useNavigate } from "react-router-dom";
import ProductCard from "./ProductCard/ProductCard";
import { Product } from "../types/Product";

interface NovedadesSectionProps {
  products: Product[];
  featuredImage: string;
  title: string;
  subtitle: string;
  onProductClick?: (productSku: string) => void;
}

export default function NovedadesSection({
  products,
  featuredImage,
  title,
  subtitle,
  onProductClick,
}: NovedadesSectionProps) {
  const [hovered, setHovered] = useState(false);
  
  const navigate = useNavigate();
  
  const handleProductClick = (productSku: string) => {
    if (onProductClick) {
      onProductClick(productSku);
    } else {
      navigate(`/productos/${productSku}`);
    }
  };

  return (
    <Box sx={{ 
      display: "flex", 
      height: 884,
      '@media (max-width: 768px)': {
        flexDirection: "column",
        height: "auto",
      },
    }}>
      <Box
        sx={{
          width: "50%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          '@media (max-width: 768px)': {
            width: "100%",
            height: "434.5px",
            order: 1,
          },
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Box
          component="img"
          src={featuredImage}
          alt={subtitle}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.6s ease, filter 0.6s ease",
            transform: hovered ? "scale(1.05)" : "scale(1)",
            filter: hovered ? "brightness(0.7)" : "brightness(1)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "4%",
            transform: "translateY(-50%)",
            textAlign: "left",
            color: "white",
            maxWidth: "80%",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 700,
              fontSize: '40px',
              mb: 1,
              '@media (max-width: 768px)': {
                fontSize: '28px',
              },
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "'Rubik', sans-serif",
              fontWeight: 400,
              fontSize: "0.95rem",
              '@media (max-width: 768px)': {
                fontSize: "0.85rem",
              },
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ 
        width: "50%", 
        height: "100%", 
        position: "relative",
        paddingBottom: '5px',
        '@media (max-width: 768px)': {
          width: "100%",
          height: "869px", 
          order: 2,
          paddingBottom: '10px',
        },
      }}>
        <Box 
          className="swiper-button-prev-custom"
          sx={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'black',
            '&::after': {
              content: '"‹"',
              fontSize: 34,
            }
          }}
        />
        <Box 
          className="swiper-button-next-custom"
          sx={{
            position: 'absolute',
            right: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'black',
            '&::after': {
              content: '"›"',
              fontSize: 34,
            }
          }}
        />

        <Box sx={{ 
          position: "relative", 
          height: "100%",
          paddingBottom: '5px',
        }}>
          <Swiper
            slidesPerView={1}
            spaceBetween={20}
            pagination={{
              clickable: true,
              el: ".novedades-pagination",
              renderBullet: function (index: number, className: string) {
                return `<span class="${className}"></span>`;
              },
            }}
            navigation={{
              nextEl: ".swiper-button-next-custom",
              prevEl: ".swiper-button-prev-custom",
            }}
            modules={[Pagination, Navigation]}
            style={{
              height: "100%",
            }}
          >
            {products.map((product) => (
              <SwiperSlide key={product.id}>
                <ProductCard
                  product={product}
                  variant="carousel"
                  onClick={() => handleProductClick(product.sku)}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          <Box
            className="novedades-pagination"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "30px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              "& .swiper-pagination-bullet": {
                width: "8px",
                height: "8px",
                margin: "0 4px",
                backgroundColor: "rgba(0,0,0,0.3)",
                opacity: 1,
                transition: 'background-color 0.3s ease-in-out',
              },
              "& .swiper-pagination-bullet-active": {
                backgroundColor: "#dab99a",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}














